<template>
  <q-page class="service-page">
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">{{ copy.title }}</div>
          <div class="page-subtitle">{{ copy.subtitle }}</div>
        </div>
        <q-btn
          class="gt-sm publish-btn"
          :to="copy.createRoute"
          icon="add_circle"
          :label="copy.createLabel"
          unelevated
        />
      </div>
    </div>

    <div class="content-wrapper">
      <div v-if="loading" class="row q-col-gutter-lg">
        <div v-for="n in 8" :key="n" class="col-12 col-sm-6 col-md-3">
          <div class="skeleton-card">
            <q-skeleton height="180px" class="skeleton-img" />
            <div class="q-pa-md">
              <q-skeleton type="text" width="60%" class="q-mb-sm" />
              <q-skeleton type="text" width="90%" class="q-mb-xs" />
              <q-skeleton type="text" width="75%" />
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="empty-state">
        <div class="empty-icon-wrap">
          <q-icon name="cloud_off" size="48px" color="grey-4" />
        </div>
        <div class="empty-title">Não foi possível carregar os dados</div>
        <div class="empty-sub">{{ error }}</div>
        <q-btn label="Tentar novamente" icon="refresh" unelevated class="empty-btn q-mt-lg" @click="load" />
      </div>

      <div v-else-if="!items.length" class="empty-state">
        <div class="empty-icon-wrap">
          <q-icon :name="copy.icon" size="48px" color="grey-4" />
        </div>
        <div class="empty-title">{{ copy.empty }}</div>
        <div class="empty-sub">{{ copy.emptySubtitle }}</div>
        <q-btn :to="copy.createRoute" :label="copy.createLabel" icon="add_circle" unelevated class="empty-btn q-mt-lg" />
      </div>

      <div v-else class="row q-col-gutter-lg">
        <div v-for="item in items" :key="item.id" class="col-12 col-sm-6 col-md-3">
          <q-card
            class="service-card"
            flat
            role="link"
            tabindex="0"
            :aria-label="`Ver detalhes de ${copy.singular}: ${item.title}`"
            @click="viewDetails(item)"
            @keydown.enter.self.prevent="viewDetails(item)"
            @keydown.space.self.prevent="viewDetails(item)"
          >
            <div class="image-container">
              <q-badge class="status-badge" :class="statusClass(item.status)">
                {{ statusLabel(item.status) }}
              </q-badge>
              <q-img :src="primaryImage(item)" :ratio="16 / 10" class="card-img">
                <template #loading><q-spinner-gears color="white" /></template>
                <template #error>
                  <div class="absolute-full image-fallback"><q-icon :name="copy.icon" size="42px" /></div>
                </template>
              </q-img>
            </div>

            <q-card-section class="card-body">
              <div class="item-type">{{ typeLabel(item) }}</div>
              <div class="item-title ellipsis-2-lines">{{ item.title }}</div>
              <div v-if="item.rejection_reason" class="rejection-note"><q-icon name="info" size="14px" /> {{ item.rejection_reason }}</div>
              <div class="location-row"><q-icon name="location_on" size="14px" /><span>{{ item.location }}</span></div>

              <div class="stats-row">
                <template v-if="kind === 'accommodation'">
                  <div class="stat"><q-icon name="meeting_room" size="16px" /><span>{{ item.units?.length || 0 }} un.</span></div>
                  <div class="stat"><q-icon name="groups" size="16px" /><span>{{ accommodationCapacity(item) }}</span></div>
                  <div class="stat"><q-icon name="bed" size="16px" /><span>{{ firstUnit(item)?.bed_amount || 0 }}</span></div>
                </template>
                <template v-else>
                  <div class="stat"><q-icon name="event_seat" size="16px" /><span>{{ item.seats || 0 }}</span></div>
                  <div class="stat"><q-icon name="settings" size="16px" /><span>{{ transmissionLabel(item.transmission) }}</span></div>
                  <div v-for="service in serviceLabels(item)" :key="service" class="stat service-stat"><q-icon name="person" size="16px" /><span>{{ service }}</span></div>
                </template>
              </div>

              <div class="card-divider" />
              <div class="price-row">
                <div class="price">{{ priceLabel(item) }}</div>
                <div class="action-btns">
                  <button v-if="['draft', 'inactive', 'rejected'].includes(item.status)" class="publish-action" title="Enviar para validação" @click.stop="publish(item)"><q-icon name="publish" size="16px" /></button>
                  <button v-else-if="item.status === 'published'" class="deactivate-action" title="Desactivar" @click.stop="deactivate(item)"><q-icon name="toggle_off" size="16px" /></button>
                  <button class="edit-btn" :title="`Editar ${copy.singular}`" @click.stop="edit(item)"><q-icon name="edit" size="16px" /></button>
                  <button class="delete-btn" :title="`Eliminar ${copy.singular}`" @click.stop="remove(item)"><q-icon name="delete" size="16px" /></button>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>

      <div v-if="!loading && !error && pagination.last_page > 1" class="pagination-row">
        <q-pagination v-model="page" :max="pagination.last_page" :max-pages="6" direction-links boundary-links color="primary" active-design="unelevated" @update:model-value="load" />
      </div>
    </div>

    <q-page-sticky position="bottom-right" :offset="[24, 24]" class="lt-md">
      <q-btn fab color="secondary" icon="add" size="xl" :to="copy.createRoute" />
    </q-page-sticky>
  </q-page>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import logo from "@/assets/logo.png";

const props = defineProps({ kind: { type: String, required: true } });
const store = useStore();
const router = useRouter();
const loading = ref(true);
const error = ref("");
const page = ref(1);
const moduleName = computed(() => props.kind === "accommodation" ? "accommodations" : "rentalVehicles");
const items = computed(() => store.getters[`${moduleName.value}/items`] || []);
const pagination = computed(() => store.getters[`${moduleName.value}/pagination`] || {});
const isAdmin = computed(() => Number(store.state.auth.user?.role) === 1);
const copy = computed(() => props.kind === "accommodation" ? {
  title: isAdmin.value ? "Todos os Alojamentos" : "Meus Alojamentos", subtitle: "Gerencie os anúncios de alojamento", icon: "hotel", singular: "alojamento",
  empty: "Nenhum alojamento publicado", emptySubtitle: "Comece por publicar o seu primeiro alojamento.", createLabel: "Publicar Alojamento", createRoute: "/alojamentos/adicionar",
} : {
  title: isAdmin.value ? "Todas as Viaturas" : "Minhas Viaturas", subtitle: "Gerencie os anúncios de rent-a-car", icon: "directions_car", singular: "viatura",
  empty: "Nenhuma viatura publicada", emptySubtitle: "Comece por publicar a sua primeira viatura.", createLabel: "Publicar Viatura", createRoute: "/rent-a-car/adicionar",
});

async function load() {
  loading.value = true;
  error.value = "";
  try {
    await store.dispatch(`${moduleName.value}/list`, {
      page: page.value,
      all: isAdmin.value,
    });
  } catch (requestError) {
    error.value = requestError.response?.data?.message || "Verifique a ligação ao servidor e tente novamente.";
  } finally {
    loading.value = false;
  }
}
function primaryImage(item) { return item.primary_image?.url || logo; }
function typeLabel(item) { return props.kind === "accommodation" ? item.type?.name || "Alojamento" : item.category?.name || "Viatura"; }
function firstUnit(item) { return item.units?.find((unit) => unit.active !== false) || item.units?.[0]; }
function accommodationCapacity(item) { return Math.max(0, ...(item.units || []).map((unit) => Number(unit.capacity || 0))); }
function transmissionLabel(value) { return value === "automatic" ? "Auto." : value === "manual" ? "Manual" : value || "—"; }
function serviceLabels(item) {
  return (item.service_options || []).filter((service) => service.active !== false).map((service) => service.service_type === "with_driver" ? "Com motorista" : "Sem motorista");
}
function statusLabel(status) {
  return { published: 'Publicado', pending_review: 'Aguarda validação', draft: 'Rascunho', rejected: 'Requer correcção', inactive: 'Inactivo', expired: 'Expirado' }[status] || status;
}
function statusClass(status) {
  if (status === 'published') return 'badge-published';
  if (status === 'pending_review') return 'badge-pending';
  if (status === 'rejected' || status === 'inactive' || status === 'expired') return 'badge-rejected';
  return 'badge-draft';
}
function priceLabel(item) {
  const unit = firstUnit(item);
  const value = props.kind === "accommodation" ? unit?.price : item.terms?.daily_price;
  const currency = props.kind === "accommodation" ? unit?.currency : item.terms?.currency;
  return value != null ? `${Number(value).toLocaleString("pt-MZ")} ${currency || "MZN"} / ${props.kind === "accommodation" ? "noite" : "dia"}` : "Preço sob consulta";
}
function viewDetails(item) { router.push(`${props.kind === "accommodation" ? "/alojamentos" : "/rent-a-car"}/${item.slug}`); }
function edit(item) { router.push(`${props.kind === "accommodation" ? "/alojamentos" : "/rent-a-car"}/editar/${item.slug}`); }
async function publish(item) {
  try {
    await store.dispatch(`${moduleName.value}/publish`, item.id);
    await load();
    Swal.fire({ title: 'Enviado para validação', text: 'Será notificado depois da análise do administrador.', icon: 'success', timer: 2200, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível publicar", requestError.response?.data?.message || "Verifique os dados obrigatórios.", "error");
  }
}
async function deactivate(item) {
  const result = await Swal.fire({ title: `Desactivar este ${copy.value.singular}?`, text: "O anúncio ficará oculto na plataforma.", icon: "warning", showCancelButton: true, confirmButtonText: "Desactivar", cancelButtonText: "Cancelar" });
  if (!result.isConfirmed) return;
  try {
    await store.dispatch(`${moduleName.value}/deactivate`, item.id);
    await load();
    Swal.fire({ title: "Anúncio desactivado", icon: "success", timer: 1800, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível desactivar", requestError.response?.data?.message || "Tente novamente.", "error");
  }
}
async function remove(item) {
  const result = await Swal.fire({ title: `Eliminar este ${copy.value.singular}?`, icon: "warning", showCancelButton: true, confirmButtonText: "Eliminar", cancelButtonText: "Cancelar" });
  if (!result.isConfirmed) return;
  try {
    await store.dispatch(`${moduleName.value}/destroy`, item.slug);
    await load();
  } catch (requestError) {
    Swal.fire("Não foi possível eliminar", requestError.response?.data?.message || "Tente novamente.", "error");
  }
}
onMounted(load);
</script>

<style scoped>
.service-page { background: #f4f6fb; min-height: 100vh; }
.page-header { background: #0d1b2a; padding: 20px 24px; }
.page-header-inner { display: flex; align-items: center; justify-content: space-between; max-width: 1400px; margin: 0 auto; gap: 12px; }
.page-title { font-size: 20px; font-weight: 700; color: #fff; }
.page-subtitle { margin-top: 2px; color: rgba(212, 175, 55, .75); font-size: 12px; letter-spacing: .4px; }
.publish-btn { background: #d4af37 !important; color: #0d1b2a !important; font-weight: 700; border-radius: 10px; font-size: 13px; letter-spacing: .3px; }
.content-wrapper { max-width: 1400px; margin: 28px auto; padding: 0 20px 48px; }
.skeleton-card { overflow: hidden; background: #fff; border: 1px solid #e8ecf4; border-radius: 16px; }
.skeleton-img { border-radius: 0; }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 16px; text-align: center; }
.empty-icon-wrap { display: flex; width: 88px; height: 88px; align-items: center; justify-content: center; margin-bottom: 20px; background: #fff; border: 1px solid #e8ecf4; border-radius: 24px; box-shadow: 0 4px 16px rgba(13, 27, 42, .06); }
.empty-title { color: #0f172a; font-size: 18px; font-weight: 700; }
.empty-sub { max-width: 480px; margin-top: 6px; color: #94a3b8; font-size: 13px; }
.empty-btn { background: #0d1b2a !important; color: #d4af37 !important; font-weight: 700; border-radius: 10px; }
.service-card { height: 100%; overflow: hidden; background: #fff; border: 1px solid #e8ecf4; border-radius: 16px; cursor: pointer; transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; }
.service-card:hover { transform: translateY(-6px); box-shadow: 0 16px 40px rgba(13, 27, 42, .12); }
.service-card:focus-visible { border-color: #d4af37; outline: 3px solid rgba(212, 175, 55, .3); outline-offset: 3px; }
.image-container { position: relative; overflow: hidden; background: #e8ecf4; }
.card-img { display: block; transition: transform .35s ease; }
.service-card:hover .card-img { transform: scale(1.05); }
.image-fallback { display: flex; align-items: center; justify-content: center; color: #d4af37; background: #0d1b2a; }
.status-badge { position: absolute; bottom: 12px; left: 12px; z-index: 1; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; letter-spacing: .4px; }
.badge-published { color: #fff !important; background: rgba(22, 163, 74, .92) !important; }
.badge-draft { color: #0d1b2a !important; background: rgba(212, 175, 55, .94) !important; }
.badge-pending { color: #fff !important; background: rgba(37, 99, 235, .92) !important; }
.badge-rejected { color: #fff !important; background: rgba(185, 28, 28, .9) !important; }
.rejection-note { display: flex; gap: 5px; margin: 0 0 9px; padding: 7px 8px; border-radius: 7px; color: #9f341e; background: #fff3ef; font-size: 10px; line-height: 1.35; }
.card-body { position: relative; z-index: 2; padding: 16px !important; }
.item-type { margin-bottom: 4px; color: #d4af37; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
.item-title { min-height: 38px; margin-bottom: 8px; color: #0f172a; font-size: 14px; font-weight: 700; line-height: 1.35; }
.ellipsis-2-lines { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; }
.location-row { display: flex; align-items: center; gap: 4px; margin-bottom: 12px; color: #64748b; font-size: 12px; }
.location-row .q-icon, .stat .q-icon { flex-shrink: 0; color: #94a3b8; }
.stats-row { display: flex; flex-wrap: wrap; gap: 10px; min-height: 22px; margin-bottom: 12px; }
.stat { display: flex; align-items: center; gap: 4px; color: #475569; font-size: 12px; font-weight: 600; }
.service-stat { flex-basis: 100%; }
.card-divider { height: 1px; margin: 12px 0; background: #f1f5f9; }
.price-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.price { color: #0d1b2a; font-size: 14px; font-weight: 800; letter-spacing: -.2px; }
.action-btns { display: flex; gap: 6px; }
.action-btns button { display: flex; width: 34px; height: 34px; flex-shrink: 0; align-items: center; justify-content: center; border: 0; border-radius: 10px; cursor: pointer; transition: background .18s, color .18s; }
.publish-action { color: #15803d; background: #f0fdf4; }
.publish-action:hover { background: #dcfce7; }
.deactivate-action { color: #b91c1c; background: #fef2f2; }
.deactivate-action:hover { background: #fee2e2; }
.edit-btn { color: #64748b; background: #f1f5f9; }
.edit-btn:hover { color: #b08b1e; background: #fef9ec; }
.delete-btn { color: #b91c1c; background: #fef2f2; }
.delete-btn:hover { background: #fee2e2; }
.pagination-row { display: flex; justify-content: center; padding-top: 36px; }
@media (max-width: 599px) { .page-header { padding: 14px 16px; } .page-title { font-size: 17px; } .page-subtitle { font-size: 11px; } .content-wrapper { margin-top: 16px; padding: 0 12px 36px; } }
</style>
